import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MessagesSquare, Briefcase, ExternalLink, ArrowLeft } from "lucide-react";
import Layout from "../components/Layout";
import ChatThread from "../components/ChatThread";
import { EmptyState } from "../components/ui/empty-state";
import { SectionLabel } from "../components/ui/section-label";
import { Button } from "../components/ui/button";
import { useChatList } from "../hooks/queries";
import type { Chat } from "../api/api";
import { algemeenChatLabel } from "../lib/chat";
import { cn } from "@/lib/utils";

function shortTime(iso: string): string {
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) {
    return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

// Admin-only, read-only overview of every partner ↔ Waves conversation.
// Threads open with ChatThread's readOnly mode, so browsing here never sends
// a message or marks anything as read for the admin.
export default function Gesprekken() {
  const navigate = useNavigate();
  const { data, isLoading } = useChatList(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<Chat | null>(null);

  const chats = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.chats ?? [])
      .filter(c => c.last_message !== null)
      .filter(c => !q
        || c.partner_name.toLowerCase().includes(q)
        || (c.job_opening_id === null ? `algemeen ${algemeenChatLabel(c)}` : c.vacature_title ?? "vacature").toLowerCase().includes(q)
        || (c.last_message?.body ?? "").toLowerCase().includes(q))
      .sort((a, b) => (b.last_message?.created_at ?? b.updated_at).localeCompare(a.last_message?.created_at ?? a.updated_at));
  }, [data, query]);

  return (
    <Layout title="Gesprekken" subtitle={isLoading ? "Laden…" : `${chats.length} gesprekken met berichten · alleen-lezen`}>
      <div className="mx-auto flex h-[calc(100dvh-170px)] min-h-[480px] max-w-[1240px] overflow-hidden rounded-card border border-waves-line bg-white">
        <div className={cn("flex w-full shrink-0 flex-col border-r border-waves-line md:w-[340px]", active && "hidden md:flex")}>
          <div className="shrink-0 border-b border-waves-line p-4">
            <div className="flex h-10 items-center gap-2 rounded-pill border border-waves-line px-4">
              <Search size={15} className="shrink-0 text-waves-muted" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Zoek partner, gebruiker, vacature of bericht…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-waves-muted"
              />
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
            {!isLoading && chats.length === 0 && <div className="py-10 text-center text-sm text-waves-muted">Geen gesprekken gevonden</div>}
            {chats.map(c => {
              const algemeen = c.job_opening_id === null;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c)}
                  className={cn("flex items-start gap-3 rounded-nav px-3 py-2.5 text-left transition-colors", active?.id === c.id ? "bg-waves-mist" : "hover:bg-waves-subtle")}
                >
                  <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-pill", algemeen ? "bg-waves-lucht" : "bg-status-neutral-bg")}>
                    {algemeen ? <MessagesSquare size={15} /> : <Briefcase size={15} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="truncate text-sm font-bold">{c.partner_name}</span>
                      <span className="ml-auto shrink-0 font-mono text-[11px] text-waves-muted">{shortTime(c.last_message?.created_at ?? c.updated_at)}</span>
                    </span>
                    <span className="block truncate text-xs font-semibold text-waves-body">{algemeen ? algemeenChatLabel(c) : c.vacature_title ?? "Vacature"}</span>
                    <span className="block truncate text-xs text-waves-muted">
                      {c.last_message ? `${c.last_message.sender_name}: ${c.last_message.body}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={cn("min-w-0 flex-1 flex-col", active ? "flex" : "hidden md:flex")}>
          {active ? (
            <>
              <div className="flex shrink-0 items-center gap-3 border-b border-waves-line px-5 py-3.5">
                <button onClick={() => setActive(null)} className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-pill hover:bg-waves-mist md:hidden" aria-label="Terug">
                  <ArrowLeft size={17} />
                </button>
                <div className="flex min-w-0 flex-1 flex-col gap-px">
                  <div className="truncate text-[15px] font-bold">{active.job_opening_id === null ? algemeenChatLabel(active) : active.vacature_title ?? "Vacature"}</div>
                  <div className="truncate text-xs text-waves-muted">{[active.partner_name, active.job_opening_id === null ? "Algemeen" : active.vacature_location].filter(Boolean).join(" · ")}</div>
                </div>
                <SectionLabel className="hidden sm:block">Alleen-lezen</SectionLabel>
                {active.job_opening_id !== null && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/vacature/${active.job_opening_id}`)}>
                    <ExternalLink /> Vacature
                  </Button>
                )}
              </div>
              <div className="min-h-0 flex-1">
                <ChatThread chat={active} readOnly />
              </div>
            </>
          ) : (
            <EmptyState
              icon={<MessagesSquare />}
              title="Kies een gesprek"
              description="Hier lees je alle gesprekken tussen partners en Waves mee. Reageren doe je via de chat rechtsonder."
              className="m-auto"
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
